import React, { useState, useEffect } from "react";
import { useBusiness, api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const ReserveRunway = () => {
  const { selectedBusiness } = useBusiness();
  const [reserveData, setReserveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    current_balance: "",
    avg_monthly_expense: "",
    target_months: 3,
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchReserveData();
    }
  }, [selectedBusiness]);

  const fetchReserveData = async () => {
    try {
      const response = await api.get(`/reserves/${selectedBusiness.id}`);
      setReserveData(response.data);
      if (response.data.current_balance > 0) {
        setFormData({
          current_balance: response.data.current_balance.toString(),
          avg_monthly_expense: response.data.avg_monthly_expense.toString(),
          target_months: response.data.target_months,
        });
      }
    } catch (error) {
      console.error("Failed to fetch reserve data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await api.post("/reserves", {
        business_id: selectedBusiness.id,
        current_balance: parseFloat(formData.current_balance) || 0,
        avg_monthly_expense: parseFloat(formData.avg_monthly_expense) || 0,
        target_months: formData.target_months,
      });
      setReserveData(response.data);
      toast.success("Reserve runway updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update reserve data");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Please select or create a business first</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const progressPercent = reserveData?.target_months > 0
    ? Math.min((reserveData.months_covered / reserveData.target_months) * 100, 100)
    : 0;

  const getStatusColor = (status) => {
    switch (status) {
      case "On Track": return "text-[#1e3a5f] bg-[#1e3a5f]/10";
      case "At Risk": return "text-amber-600 bg-amber-100";
      default: return "text-stone-600 bg-stone-100";
    }
  };

  return (
    <div className="space-y-8" data-testid="reserve-runway-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Reserve Runway Tracker</h1>
        <p className="text-stone-600">Monitor how many months your reserves can cover expenses</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Current Status */}
        <Card className="lg:row-span-2">
          <CardHeader>
            <CardTitle>Runway Status</CardTitle>
            <CardDescription>Your current financial safety net</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Main Metric */}
            <div className="text-center py-8">
              <div className="inline-flex items-baseline gap-1">
                <span className="text-7xl font-bold text-stone-900" data-testid="months-covered">
                  {reserveData?.months_covered?.toFixed(1) || "0.0"}
                </span>
                <span className="text-2xl text-stone-500">months</span>
              </div>
              <p className="text-stone-600 mt-2">of operating expenses covered</p>
              <div className={`inline-flex px-4 py-2 rounded-full mt-4 font-medium ${getStatusColor(reserveData?.status)}`}>
                {reserveData?.status || "Not Set"}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Progress to target</span>
                <span className="font-medium">{progressPercent.toFixed(0)}%</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
              <div className="flex justify-between text-xs text-stone-500">
                <span>0 months</span>
                <span>Target: {reserveData?.target_months || 3} months</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-stone-50">
                <p className="text-sm text-stone-500">Reserve Balance</p>
                <p className="text-2xl font-bold text-stone-900" data-testid="current-balance">
                  ${reserveData?.current_balance?.toLocaleString() || "0"}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-stone-50">
                <p className="text-sm text-stone-500">Monthly Expenses</p>
                <p className="text-2xl font-bold text-stone-900" data-testid="monthly-expense">
                  ${reserveData?.avg_monthly_expense?.toLocaleString() || "0"}
                </p>
              </div>
            </div>

            {/* Recommendations */}
            {reserveData?.status === "At Risk" && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                  <span>⚠️</span> Action Needed
                </h4>
                <p className="text-sm text-amber-700 mt-2">
                  Your reserves cover less than your target. Consider:
                </p>
                <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
                  <li>Reducing non-essential operating expenses</li>
                  <li>Increasing your reserve contributions</li>
                  <li>Finding additional revenue streams</li>
                </ul>
              </div>
            )}

            {reserveData?.status === "On Track" && (
              <div className="p-4 rounded-xl bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
                <h4 className="font-semibold text-[#1e3a5f] flex items-center gap-2">
                  <span>✅</span> Looking Good!
                </h4>
                <p className="text-sm text-[#1e3a5f] mt-2">
                  Your reserves meet or exceed your target. Keep maintaining this healthy buffer.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Update Form */}
        <Card>
          <CardHeader>
            <CardTitle>Update Reserve Data</CardTitle>
            <CardDescription>Enter your current reserve balance and expenses</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="balance">Current Reserve Balance</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                  <Input
                    id="balance"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.current_balance}
                    onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                    className="pl-8 h-12"
                    placeholder="10000.00"
                    data-testid="reserve-balance-input"
                  />
                </div>
                <p className="text-xs text-stone-500">
                  Total amount in your operating expense reserve account
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expense">Average Monthly Operating Expense</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                  <Input
                    id="expense"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.avg_monthly_expense}
                    onChange={(e) => setFormData({ ...formData, avg_monthly_expense: e.target.value })}
                    className="pl-8 h-12"
                    placeholder="3000.00"
                    data-testid="monthly-expense-input"
                  />
                </div>
                <p className="text-xs text-stone-500">
                  Average of your monthly operating expenses
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target">Target Runway (Months)</Label>
                <Input
                  id="target"
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={formData.target_months}
                  onChange={(e) => setFormData({ ...formData, target_months: parseFloat(e.target.value) || 3 })}
                  className="h-12"
                  data-testid="target-months-input"
                />
                <p className="text-xs text-stone-500">
                  Recommended: 3 months for established businesses, 6+ for newer ones
                </p>
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                data-testid="save-reserve-btn"
              >
                {saving ? "Saving..." : "Update Runway"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>What is Reserve Runway?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-stone-600">
              Your reserve runway tells you how many months your business can operate if revenue 
              suddenly stopped. It's your financial safety net.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#1e3a5f] font-bold">1</span>
                </div>
                <div>
                  <p className="font-medium text-stone-900">Calculate your monthly burn</p>
                  <p className="text-sm text-stone-500">Add up all your monthly operating expenses</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#1e3a5f] font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium text-stone-900">Check your reserves</p>
                  <p className="text-sm text-stone-500">Total cash in your reserve accounts</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#1e3a5f] font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium text-stone-900">Divide to get runway</p>
                  <p className="text-sm text-stone-500">Reserves ÷ Monthly Expenses = Months of Runway</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReserveRunway;